import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Pagination,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon, Clear as ClearIcon, Add as AddIcon } from '@mui/icons-material';
import { BaseButton, BaseTextField } from '../../../base';
import { ConfigScheme } from '../../../../../services/ConfigManagementService';
import { CollectionTable } from './CollectionTable';
import { CollectionFiltersState } from '../../../../../hooks/useCollectionFilters';

const COLORS = {
  ACCENT: '#005e5d',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
};

export interface CollectionTabProps {
  schemes: ConfigScheme[];
  selectedCollectionKey: string | null;
  collectionItems: any[];
  collectionPaging: CollectionFiltersState;
  collectionSearch: string;
  collectionSortBy: string;
  collectionSortOrder: 'asc' | 'desc';
  editingRowId: string | null;
  editingRowData: Record<string, any>;
  lookupCache: Record<string, Record<string, any>>;
  loading: boolean;
  saving: boolean;
  schema: Record<string, any>;
  
  // Event handlers
  onSelectCollection: (key: string | null) => void;
  onSearch: (query: string) => void;
  onPageChange: (page: number) => void;
  onSort: (column: string) => void;
  onCreateItem: () => void;
  onRefresh: () => void;
  
  // Collection table handlers
  onStartEditRow: (item: any) => void;
  onCancelEditRow: () => void;
  onSaveEditRow: () => void;
  onEditCellChange: (field: string, value: any) => void;
  onEditCollectionItem: (id: string) => void;
  onDeleteCollectionItem: (id: string) => void;
}

/**
 * Collection management tab
 * Sidebar with collection schemas + main area with table
 * Layout matches ConfigurationTab for consistency
 */
export const CollectionTab: React.FC<CollectionTabProps> = ({
  schemes,
  selectedCollectionKey,
  collectionItems,
  collectionPaging,
  collectionSearch,
  collectionSortBy,
  collectionSortOrder,
  editingRowId,
  editingRowData,
  lookupCache,
  loading,
  saving,
  schema,
  onSelectCollection,
  onSearch,
  onPageChange,
  onSort,
  onCreateItem,
  onRefresh,
  onStartEditRow,
  onCancelEditRow,
  onSaveEditRow,
  onEditCellChange,
  onEditCollectionItem,
  onDeleteCollectionItem,
}) => {
  const collectionSchemes = schemes.filter((s) => s.type === 'collection');
  const selectedScheme = collectionSchemes.find((s) => s.key === selectedCollectionKey);

  return (
    <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Collection Management
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={onRefresh} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {collectionSchemes.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No collection schemas found. Create a collection type schema first.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, px: 2, pb: 2, flex: 1, overflow: 'hidden' }}>
          {/* SIDEBAR - Collection Schema List */}
          <Paper
            elevation={0}
            sx={{
              width: '250px',
              border: `1px solid ${COLORS.BORDER}`,
              borderRadius: '14px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fafafa',
            }}
          >
            <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderBottom: `1px solid ${COLORS.BORDER}` }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.TEXT_SECONDARY }}>
                COLLECTIONS
              </Typography>
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
              {collectionSchemes.map((scheme, index) => (
                <Box key={scheme.key}>
                  <ListItemButton
                    selected={selectedCollectionKey === scheme.key}
                    onClick={() => onSelectCollection(scheme.key)}
                    disabled={loading}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: COLORS.BORDER,
                        borderLeft: `4px solid ${COLORS.ACCENT}`,
                        '&:hover': { bgcolor: COLORS.BORDER },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: selectedCollectionKey === scheme.key ? 600 : 500,
                            color: selectedCollectionKey === scheme.key ? COLORS.ACCENT : COLORS.TEXT_PRIMARY,
                          }}
                        >
                          {scheme.label || scheme.key}
                        </Typography>
                      }
                      secondary={
                        scheme.description && (
                          <Typography variant="caption" sx={{ fontSize: '11px', color: COLORS.TEXT_TERTIARY }}>
                            {scheme.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                  {index < collectionSchemes.length - 1 && <Divider sx={{ my: 0 }} />}
                </Box>
              ))}
            </List>
          </Paper>

          {/* MAIN CONTENT - Collection Table */}
          {selectedScheme ? (
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '14px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${COLORS.BORDER}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedScheme.label || selectedScheme.key}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {collectionPaging.total} items
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={onRefresh} disabled={loading}>
                    <RefreshIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Toolbar - Search and Create */}
              <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.BORDER}`, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <BaseTextField
                    placeholder="Search items..."
                    value={collectionSearch}
                    onChange={(e) => onSearch(e.target.value)}
                    startIcon={<SearchIcon />}
                    endIcon={collectionSearch && (
                      <IconButton
                        size="small"
                        onClick={() => onSearch('')}
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  />
                </Box>
                <BaseButton
                  variant="contained"
                  colorVariant="primary"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={onCreateItem}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Create Item
                </BaseButton>
              </Box>

              {/* Table Content */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                {collectionItems.length > 0 ? (
                  <Box>
                    <CollectionTable
                      items={collectionItems}
                      schemaKey={selectedCollectionKey}
                      schema={schema}
                      editingRowId={editingRowId}
                      editingRowData={editingRowData}
                      sortBy={collectionSortBy}
                      sortOrder={collectionSortOrder}
                      lookupCache={lookupCache}
                      saving={saving}
                      onStartEdit={onStartEditRow}
                      onCancelEdit={onCancelEditRow}
                      onSaveEdit={onSaveEditRow}
                      onEditCellChange={onEditCellChange}
                      onSort={onSort}
                      onEdit={onEditCollectionItem}
                      onDelete={onDeleteCollectionItem}
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                      No items in this collection yet.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Footer - Pagination */}
              {collectionItems.length > 0 && collectionPaging.pages > 1 && (
                <Box
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${COLORS.BORDER}`,
                    bgcolor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Pagination
                    count={collectionPaging.pages}
                    page={collectionPaging.page}
                    onChange={(e, page) => onPageChange(page)}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${COLORS.BORDER}`,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="textSecondary">
                Select a collection schema to view items
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CollectionTab;
